import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const JobTemplateLibrary = ({ onLoadTemplate, onClose }) => {
  const templates = [
    {
      id: 1,
      name: 'Software Engineer - Full Stack',
      category: 'Engineering',
      data: {
        title: 'Full Stack Software Engineer',
        company: '',
        companyDescription: '',
        responsibilities: '• Design, develop, and maintain scalable web applications\n• Collaborate with cross-functional teams to define and ship new features\n• Write clean, maintainable, and efficient code\n• Participate in code reviews and mentor junior developers\n• Troubleshoot and debug applications',
        qualifications: '• Bachelor\'s degree in Computer Science or related field\n• 2+ years of experience in full-stack development\n• Strong problem-solving skills\n• Excellent communication and teamwork abilities',
        type: 'Full Time',
        location: '',
        workArrangement: 'hybrid',
        salaryMin: '6',
        salaryMax: '12',
        educationLevel: ['bachelors', 'masters'],
        requiredSkills: ['React.js (advanced)', 'Node.js (intermediate)', 'MongoDB (intermediate)', 'JavaScript (advanced)'],
        experienceMin: '2',
        experienceMax: '5'
      }
    },
    {
      id: 2,
      name: 'Marketing Intern',
      category: 'Marketing',
      data: {
        title: 'Digital Marketing Intern',
        company: '',
        companyDescription: '',
        responsibilities: '• Assist in developing and executing digital marketing campaigns\n• Create engaging content for social media platforms\n• Analyze campaign performance and prepare reports\n• Support SEO and SEM initiatives\n• Collaborate with the marketing team on various projects',
        qualifications: '• Currently pursuing or recently completed degree in Marketing, Communications, or related field\n• Basic understanding of digital marketing concepts\n• Creative mindset with strong writing skills\n• Familiarity with social media platforms',
        type: 'Internship',
        location: '',
        workArrangement: 'remote',
        salaryMin: '0',
        salaryMax: '0',
        educationLevel: ['bachelors'],
        requiredSkills: ['Social Media Marketing (beginner)', 'Content Writing (intermediate)', 'Google Analytics (beginner)'],
        experienceMin: '0',
        experienceMax: '1',
        isInternship: true,
        internshipDuration: '3-months',
        stipendAmount: '10000',
        hasMentorship: true,
        mentorshipDetails: '• Weekly one-on-one sessions with marketing manager\n• Hands-on training in digital marketing tools\n• Career guidance and skill development workshops'
      }
    },
    {
      id: 3,
      name: 'Data Analyst',
      category: 'Data Science',
      data: {
        title: 'Data Analyst',
        company: '',
        companyDescription: '',
        responsibilities: '• Collect, process, and analyze large datasets\n• Create data visualizations and dashboards\n• Identify trends and patterns to support business decisions\n• Collaborate with stakeholders to understand data requirements\n• Maintain data quality and integrity',
        qualifications: '• Bachelor\'s degree in Statistics, Mathematics, Computer Science, or related field\n• 1-3 years of experience in data analysis\n• Strong analytical and problem-solving skills\n• Attention to detail and accuracy',
        type: 'Full Time',
        location: '',
        workArrangement: 'onsite',
        salaryMin: '5',
        salaryMax: '9',
        educationLevel: ['bachelors', 'masters'],
        requiredSkills: ['Python (intermediate)', 'SQL (advanced)', 'Excel (advanced)', 'Tableau (intermediate)'],
        experienceMin: '1',
        experienceMax: '3'
      }
    },
    {
      id: 4,
      name: 'UI/UX Designer',
      category: 'Design',
      data: {
        title: 'UI/UX Designer',
        company: '',
        companyDescription: '',
        responsibilities: '• Design user interfaces for web and mobile applications\n• Conduct user research and usability testing\n• Create wireframes, prototypes, and high-fidelity mockups\n• Collaborate with developers to ensure design implementation\n• Maintain and evolve design systems',
        qualifications: '• Bachelor\'s degree in Design, HCI, or related field\n• 2+ years of UI/UX design experience\n• Strong portfolio demonstrating design skills\n• Understanding of user-centered design principles',
        type: 'Full Time',
        location: '',
        workArrangement: 'hybrid',
        salaryMin: '5',
        salaryMax: '10',
        educationLevel: ['bachelors', 'masters'],
        requiredSkills: ['Figma (advanced)', 'Adobe XD (intermediate)', 'User Research (intermediate)', 'Prototyping (advanced)'],
        experienceMin: '2',
        experienceMax: '5'
      }
    }
  ];

  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', ...new Set(templates?.map((t) => t?.category))];

  const filteredTemplates = selectedCategory === 'All'
    ? templates
    : templates?.filter((t) => t?.category === selectedCategory);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-heading font-semibold text-card-foreground">
              Job Template Library
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Select a template to quickly create a job posting
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-border">
          <div className="flex flex-wrap gap-2">
            {categories?.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-smooth ${
                  selectedCategory === category
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredTemplates?.map((template) => (
              <div
                key={template?.id}
                className="bg-background border border-border rounded-lg p-4 hover:shadow-warm-md transition-smooth"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-medium text-card-foreground mb-1">
                      {template?.name}
                    </h3>
                    <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-secondary/10 text-secondary">
                      {template?.category}
                    </span>
                  </div>
                  <Icon name="FileText" size={24} color="var(--color-primary)" />
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Briefcase" size={14} color="currentColor" />
                    <span>{template?.data?.type}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="MapPin" size={14} color="currentColor" />
                    <span>{template?.data?.workArrangement?.charAt(0)?.toUpperCase() + template?.data?.workArrangement?.slice(1)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="GraduationCap" size={14} color="currentColor" />
                    <span>{template?.data?.educationLevel?.length} education levels</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Target" size={14} color="currentColor" />
                    <span>{template?.data?.requiredSkills?.length} skills required</span>
                  </div>
                </div>

                <Button
                  onClick={() => onLoadTemplate(template?.data)}
                  fullWidth
                  iconName="Download"
                  iconPosition="left"
                >
                  Use Template
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobTemplateLibrary;